import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/integrations/supabase/role-middleware";
import { hofEntryInput } from "@/lib/hall-of-fame.helpers";

const hofEntryFields =
  "id, title, subtitle, body, entry_year, media_type, media_url, published, sort_order, created_at, updated_at";

export const listPublishedHallOfFameEntries = createServerFn({ method: "GET" }).handler(
  async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("hall_of_fame_entries")
      .select(hofEntryFields)
      .eq("published", true)
      .order("entry_year", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  },
);

export const listAllHallOfFameEntries = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("hall_of_fame_entries")
      .select(hofEntryFields)
      .order("entry_year", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const createHallOfFameEntry = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => hofEntryInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: entry, error } = await supabase
      .from("hall_of_fame_entries")
      .insert({
        title: data.title,
        subtitle: data.subtitle,
        body: data.body ?? "",
        entry_year: data.entry_year,
        media_type: data.media_type,
        media_url: data.media_url,
        published: data.published ?? true,
        sort_order: data.sort_order ?? 0,
      })
      .select(hofEntryFields)
      .single();
    if (error) throw new Error(error.message);
    return entry;
  });

export const updateHallOfFameEntry = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => hofEntryInput.extend({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: entry, error } = await supabase
      .from("hall_of_fame_entries")
      .update({
        title: data.title,
        subtitle: data.subtitle,
        body: data.body ?? "",
        entry_year: data.entry_year,
        media_type: data.media_type,
        media_url: data.media_url,
        published: data.published ?? true,
        ...(data.sort_order !== undefined ? { sort_order: data.sort_order } : {}),
      })
      .eq("id", data.id)
      .select(hofEntryFields)
      .single();
    if (error) throw new Error(error.message);
    return entry;
  });

export const deleteHallOfFameEntry = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { error } = await supabase.from("hall_of_fame_entries").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
